import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select from 'react-select';
import { filter, flow, map, sortBy } from 'lodash/fp';

import api from 'lib/api';
import { ADMIN_DIVISIONS } from 'lib/config';
import theme from 'styles/theme';
import Box from 'components/box';
import Button from 'components/button';
import { Alert } from 'components/alerts';
import LoadingIndicator from 'components/loading-indicator';

const EMPTY_VALUE = '-1';

/** Branch admin levels (gote excluded). */
const BRANCH_PICKER_LEVEL_ORDER = [
  ADMIN_DIVISIONS.COUNTRY,
  ADMIN_DIVISIONS.REGION,
  ADMIN_DIVISIONS.ZONE,
  ADMIN_DIVISIONS.WOREDA,
  ADMIN_DIVISIONS.KEBELE,
];

const LEVEL_LABELS = {
  [ADMIN_DIVISIONS.COUNTRY]: 'Country',
  [ADMIN_DIVISIONS.REGION]: 'Region',
  [ADMIN_DIVISIONS.ZONE]: 'Zone',
  [ADMIN_DIVISIONS.WOREDA]: 'Woreda',
  [ADMIN_DIVISIONS.KEBELE]: 'Kebele',
};

const branchSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: state.isFocused ? theme.colors.blue[6] : theme.colors.gray[3],
    backgroundColor: state.isDisabled ? theme.colors.gray[0] : theme.colors.white,
    boxShadow: state.isFocused ? `0 0 0 3px ${theme.colors.blue[1]}` : 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      borderColor: state.isDisabled ? theme.colors.gray[3] : theme.colors.blue[5],
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 10px',
  }),
  placeholder: base => ({
    ...base,
    color: theme.colors.gray[5],
    fontSize: theme.font.size[1],
  }),
  singleValue: base => ({
    ...base,
    color: theme.colors.black,
    fontSize: theme.font.size[1],
  }),
  input: base => ({
    ...base,
    fontSize: theme.font.size[1],
    margin: 0,
    padding: 0,
  }),
  menu: base => ({
    ...base,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(32, 33, 36, 0.12)',
    zIndex: 20,
  }),
  menuList: base => ({
    ...base,
    padding: 4,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: theme.font.size[1],
    borderRadius: 6,
    padding: '7px 10px',
    backgroundColor: state.isSelected
      ? theme.colors.blue[6]
      : state.isFocused
        ? theme.colors.blue[0]
        : theme.colors.white,
    color: state.isSelected ? theme.colors.white : theme.colors.black,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? theme.colors.blue[6] : theme.colors.gray[5],
    padding: '0 6px',
    '&:hover': { color: theme.colors.blue[6] },
  }),
};

const PickerSection = styled.section`
  width: 100%;
  background: white;
  border-bottom: 1px solid ${props => props.theme.colors.gray[2]};
  padding: 12px 24px;
`;

const PickerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  width: 100%;
`;

const PickerFieldWrap = styled.div`
  flex: 1 1 150px;
  min-width: 130px;
  max-width: 210px;
`;

const PickerLoadWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  margin-left: auto;
`;

const PickerLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.gray[6]};
`;


function parseSelectionId(value) {
  return value === EMPTY_VALUE ? undefined : parseInt(value, 10);
}

function getLevelIndex(level) {
  const index = BRANCH_PICKER_LEVEL_ORDER.indexOf(level);
  return index === -1 ? BRANCH_PICKER_LEVEL_ORDER.length : index;
}

function buildDivisionPath(divisionsById, divisionId) {
  if (!divisionId) return [];

  const path = [];
  let current = divisionsById[divisionId];
  while (current) {
    path.unshift(current);
    current = current.parentId ? divisionsById[current.parentId] : undefined;
  }
  return path;
}

function isDescendantOf(divisionsById, division, ancestorId) {
  let current = division;
  while (current) {
    if (current.id === ancestorId) return true;
    current = current.parentId ? divisionsById[current.parentId] : undefined;
  }
  return false;
}

/** Levels below the assigned division that exist in branch data. */
function getPickerLevelsBelowAssigned(divisions, assignedDivision) {
  if (!assignedDivision) return [];

  const assignedLevelIndex = getLevelIndex(assignedDivision.level);
  const levelsInData = new Set(divisions.map(division => division.level));

  return BRANCH_PICKER_LEVEL_ORDER.filter(
    level => getLevelIndex(level) > assignedLevelIndex && levelsInData.has(level),
  );
}

function buildOptionsForLevel(
  divisions,
  level,
  pickerLevels,
  levelSelections,
  divisionsById,
  assignedDivisionId,
) {
  const levelIndex = pickerLevels.indexOf(level);
  const parentLevel = levelIndex === 0 ? null : pickerLevels[levelIndex - 1];
  const parentId = parentLevel
    ? parseSelectionId(levelSelections[parentLevel])
    : assignedDivisionId;

  if (!parentId) return [];

  return flow(
    filter(
      division => division.level === level && isDescendantOf(divisionsById, division, parentId),
    ),
    map(division => ({ value: String(division.id), label: division.name })),
    sortBy(option => option.label),
  )(divisions);
}

function resolveSelectedDivisionId(assignedDivisionId, pickerLevels, levelSelections) {
  for (let i = pickerLevels.length - 1; i >= 0; i -= 1) {
    const level = pickerLevels[i];
    const selected = levelSelections[level];
    if (selected && selected !== EMPTY_VALUE) {
      return parseSelectionId(selected);
    }
  }
  return assignedDivisionId;
}

function findSelectedOption(options, value) {
  if (!value || value === EMPTY_VALUE) return null;
  return options.find(option => String(option.value) === String(value)) || null;
}

function PickerSelect({
  label,
  name,
  options,
  value,
  onChange,
  isDisabled,
  placeholder,
}) {
  return (
    <PickerFieldWrap>
      <PickerLabel htmlFor={name}>{label}</PickerLabel>
      <Select
        inputId={name}
        name={name}
        styles={branchSelectStyles}
        options={options}
        value={findSelectedOption(options, value)}
        onChange={onChange}
        isDisabled={isDisabled}
        placeholder={placeholder}
        isSearchable
        isClearable={false}
        classNamePrefix="branch-picker"
      />
    </PickerFieldWrap>
  );
}

PickerSelect.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

PickerSelect.defaultProps = {
  value: EMPTY_VALUE,
  isDisabled: false,
  placeholder: 'Select…',
};

function buildSelectionPayload({
  selectedEnrollmentYearId,
  selectedEnrollmentYear,
  selectedBranchId,
  selectedBranch,
  parsedAssignedDivisionId,
  assignedDivision,
  selectedDivisionId,
  selectedPath,
  pickerLevels,
}) {
  return {
    enrollmentYearId: selectedEnrollmentYearId,
    enrollmentYear: selectedEnrollmentYear,
    branchId: selectedBranchId,
    branchName: selectedBranch ? selectedBranch.name : undefined,
    assignedAdministrativeDivisionId: parsedAssignedDivisionId,
    assignedAdministrativeDivision: assignedDivision,
    administrativeDivisionId: selectedDivisionId,
    administrativeDivision: selectedPath.length ? selectedPath[selectedPath.length - 1] : undefined,
    path: selectedPath,
    pickerLevels,
  };
}

function BranchAdministrativeDivisionPicker({
  initialBranchId,
  initialEnrollmentYearId,
  onSelectionChange,
  onLoad,
  isLoadingLoad,
  autoLoad,
  autoEnrollmentYearId,
}) {
  const [enrollmentYears, setEnrollmentYears] = useState([]);
  const [enrollmentYearId, setEnrollmentYearId] = useState(
    initialEnrollmentYearId ? String(initialEnrollmentYearId) : EMPTY_VALUE,
  );
  const [branches, setBranches] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [assignedDivisionsList, setAssignedDivisionsList] = useState([]);
  const [branchId, setBranchId] = useState(initialBranchId ? String(initialBranchId) : EMPTY_VALUE);
  const [assignedDivisionId, setAssignedDivisionId] = useState(EMPTY_VALUE);
  const [levelSelections, setLevelSelections] = useState({});
  const [isLoadingEnrollmentYears, setIsLoadingEnrollmentYears] = useState(true);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);
  const [isLoadingDivisions, setIsLoadingDivisions] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;

    setIsLoadingEnrollmentYears(true);
    api.fetchEnrollmentYears()
      .then((data) => {
        if (cancelled) return;
        setEnrollmentYears(data);
        const autoYear = autoEnrollmentYearId
          && data.find(year => String(year.id) === String(autoEnrollmentYearId));
        if (autoYear) {
          setEnrollmentYearId(String(autoEnrollmentYearId));
        } else if (initialEnrollmentYearId) {
          setEnrollmentYearId(String(initialEnrollmentYearId));
        } else if (data.length === 1) {
          setEnrollmentYearId(String(data[0].id));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load enrollment years');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEnrollmentYears(false);
      });

    return () => { cancelled = true; };
  }, [initialEnrollmentYearId, autoEnrollmentYearId]);

  useEffect(() => {
    let cancelled = false;

    setIsLoadingBranches(true);
    api.fetchBranches()
      .then((data) => {
        if (cancelled) return;
        setBranches(data);
        if (autoLoad && data.length) {
          // Auto mode (branch managers): preselect the first branch in the dropdown.
          setBranchId(String(data[0].id));
        } else if (initialBranchId) {
          setBranchId(String(initialBranchId));
        } else if (data.length === 1) {
          setBranchId(String(data[0].id));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load branches');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBranches(false);
      });

    return () => { cancelled = true; };
  }, [initialBranchId, autoLoad]);

  useEffect(() => {
    const selectedBranchId = parseSelectionId(branchId);
    if (!selectedBranchId) {
      setDivisions([]);
      setAssignedDivisionsList([]);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingDivisions(true);
    setLoadError('');

    Promise.all([
      api.fetchAdministrativeDivisions(selectedBranchId),
      api.fetchAdministrativeDivisions(selectedBranchId, { assignedOnly: true }),
    ])
      .then(([allDivisions, assignedOnly]) => {
        if (cancelled) return;
        setDivisions(allDivisions);
        setAssignedDivisionsList(
          flow(sortBy(division => division.name))(assignedOnly),
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load administrative divisions');
          setDivisions([]);
          setAssignedDivisionsList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDivisions(false);
      });

    return () => { cancelled = true; };
  }, [branchId]);

  useEffect(() => {
    setAssignedDivisionId(EMPTY_VALUE);
    setLevelSelections({});
  }, [branchId]);

  const selectedBranchId = parseSelectionId(branchId);

  const assignedDivisions = assignedDivisionsList;

  useEffect(() => {
    if (assignedDivisions.length === 1 && assignedDivisionId === EMPTY_VALUE) {
      setAssignedDivisionId(String(assignedDivisions[0].id));
    }
  }, [assignedDivisions, assignedDivisionId]);

  const divisionsById = useMemo(
    () => divisions.reduce((acc, division) => ({ ...acc, [division.id]: division }), {}),
    [divisions],
  );

  const parsedAssignedDivisionId = parseSelectionId(assignedDivisionId);
  const assignedDivision = parsedAssignedDivisionId
    ? divisionsById[parsedAssignedDivisionId]
    : undefined;

  const pickerLevels = useMemo(
    () => getPickerLevelsBelowAssigned(divisions, assignedDivision),
    [divisions, assignedDivision],
  );

  const selectedDivisionId = resolveSelectedDivisionId(
    parsedAssignedDivisionId,
    pickerLevels,
    levelSelections,
  );
  const selectedPath = buildDivisionPath(divisionsById, selectedDivisionId);
  const selectedBranch = branches.find(branch => String(branch.id) === branchId);
  const selectedEnrollmentYearId = parseSelectionId(enrollmentYearId);
  const selectedEnrollmentYear = enrollmentYears.find(
    year => String(year.id) === enrollmentYearId,
  );

  useEffect(() => {
    setLevelSelections({});
  }, [assignedDivisionId]);

  useEffect(() => {
    if (!onSelectionChange) return;

    const path = buildDivisionPath(divisionsById, selectedDivisionId);
    onSelectionChange(buildSelectionPayload({
      selectedEnrollmentYearId,
      selectedEnrollmentYear,
      selectedBranchId,
      selectedBranch,
      parsedAssignedDivisionId,
      assignedDivision,
      selectedDivisionId,
      selectedPath: path,
      pickerLevels,
    }));
  }, [
    enrollmentYearId,
    selectedEnrollmentYearId,
    selectedEnrollmentYear,
    branchId,
    selectedDivisionId,
    selectedBranch,
    assignedDivision,
    parsedAssignedDivisionId,
    divisionsById,
    pickerLevels,
    onSelectionChange,
    selectedBranchId,
  ]);

  const handleEnrollmentYearChange = (option) => {
    setEnrollmentYearId(option ? String(option.value) : EMPTY_VALUE);
  };

  const handleLoadClick = () => {
    if (!onLoad) return;

    onLoad(buildSelectionPayload({
      selectedEnrollmentYearId,
      selectedEnrollmentYear,
      selectedBranchId,
      selectedBranch,
      parsedAssignedDivisionId,
      assignedDivision,
      selectedDivisionId,
      selectedPath,
      pickerLevels,
    }));
  };

  // Auto mode: once the year + first branch are selected and divisions have settled, fire
  // the load once without any user interaction (whole-branch view; no division required).
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (!autoLoad || autoFiredRef.current || !onLoad) return;
    if (isLoadingEnrollmentYears || isLoadingBranches || isLoadingDivisions) return;
    if (enrollmentYearId === EMPTY_VALUE || branchId === EMPTY_VALUE) return;

    autoFiredRef.current = true;
    handleLoadClick();
  }, [autoLoad, isLoadingEnrollmentYears, isLoadingBranches, isLoadingDivisions, enrollmentYearId, branchId]);

  const handleBranchChange = (option) => {
    setBranchId(option ? String(option.value) : EMPTY_VALUE);
  };

  const handleAssignedDivisionChange = (option) => {
    setAssignedDivisionId(option ? String(option.value) : EMPTY_VALUE);
  };

  const handleLevelChange = level => (option) => {
    const value = option ? String(option.value) : EMPTY_VALUE;
    setLevelSelections((prev) => {
      const next = { ...prev, [level]: value };
      const levelIndex = pickerLevels.indexOf(level);
      pickerLevels.slice(levelIndex + 1).forEach((deeperLevel) => {
        next[deeperLevel] = EMPTY_VALUE;
      });
      return next;
    });
  };

  const isLevelDisabled = (levelIndex) => {
    if (!parsedAssignedDivisionId) return true;
    if (levelIndex === 0) return false;
    const parentLevel = pickerLevels[levelIndex - 1];
    const parentValue = levelSelections[parentLevel];
    return !parentValue || parentValue === EMPTY_VALUE;
  };

  const enrollmentYearOptions = flow(
    sortBy(year => year.year),
    map(year => ({
      value: String(year.id),
      label: String(year.year),
    })),
  )(enrollmentYears);

  const branchOptions = branches.map(branch => ({
    value: String(branch.id),
    label: branch.name,
  }));

  const assignedDivisionOptions = assignedDivisions.map(division => ({
    value: String(division.id),
    label: `${division.name} (${LEVEL_LABELS[division.level] || division.level})`,
  }));

  const enrollmentYearSelected = enrollmentYearId !== EMPTY_VALUE;
  const branchSelected = branchId !== EMPTY_VALUE;
  const isPickerDisabled = !branchSelected || isLoadingDivisions || !parsedAssignedDivisionId;
  const isInitialLoading = isLoadingEnrollmentYears || isLoadingBranches;
  const canLoad = Boolean(
    onLoad
    && enrollmentYearSelected
    && branchSelected
    && !isLoadingDivisions,
  );
  if (isInitialLoading) {
    return (
      <PickerSection>
        <LoadingIndicator noun="filters" />
      </PickerSection>
    );
  }

  return (
    <PickerSection>
      {loadError && (
        <Box marginBottom={3}>
          <Alert>{loadError}</Alert>
        </Box>
      )}

      <PickerRow>
        <PickerSelect
          name="enrollmentYear"
          label="Enrollment year"
          options={enrollmentYearOptions}
          value={enrollmentYearId}
          onChange={handleEnrollmentYearChange}
          isDisabled={enrollmentYearOptions.length === 0}
          placeholder="Select enrollment year"
        />

        <PickerSelect
          name="branch"
          label="Branch"
          options={branchOptions}
          value={branchId}
          onChange={handleBranchChange}
          isDisabled={!enrollmentYearSelected}
          placeholder="Select branch"
        />

        {branchSelected && !isLoadingDivisions && (
          <PickerSelect
            name="assignedAdministrativeDivision"
            label="Assigned admin division"
            options={assignedDivisionOptions}
            value={assignedDivisionId}
            onChange={handleAssignedDivisionChange}
            isDisabled={assignedDivisionOptions.length === 0}
            placeholder="Select assigned division"
          />
        )}

        {branchSelected && !isLoadingDivisions && parsedAssignedDivisionId && pickerLevels.map((level, levelIndex) => {
          const levelLabel = LEVEL_LABELS[level] || level;
          const options = buildOptionsForLevel(
            divisions,
            level,
            pickerLevels,
            levelSelections,
            divisionsById,
            parsedAssignedDivisionId,
          );

          return (
            <PickerSelect
              key={level}
              name={level}
              label={levelLabel}
              options={options}
              value={levelSelections[level] || EMPTY_VALUE}
              onChange={handleLevelChange(level)}
              isDisabled={isPickerDisabled || isLevelDisabled(levelIndex)}
              placeholder={`Select ${levelLabel.toLowerCase()}`}
            />
          );
        })}

        {onLoad && (
          <PickerLoadWrap>
            <Button
              type="button"
              primary
              inline
              height="36px"
              disabled={!canLoad || isLoadingLoad}
              onClick={handleLoadClick}
            >
              {isLoadingLoad ? 'Loading…' : 'Load'}
            </Button>
          </PickerLoadWrap>
        )}
      </PickerRow>

      {isLoadingDivisions && branchSelected && (
        <Box marginTop={3}>
          <LoadingIndicator noun="administrative divisions" />
        </Box>
      )}

      {branchSelected && !isLoadingDivisions && assignedDivisionOptions.length === 0 && (
        <Box marginTop={3}>
          <Alert>No administrative divisions are assigned to this branch.</Alert>
        </Box>
      )}

      {!isLoadingEnrollmentYears && enrollmentYearOptions.length === 0 && (
        <Box marginTop={3}>
          <Alert>No enrollment years are configured.</Alert>
        </Box>
      )}
    </PickerSection>
  );
}

BranchAdministrativeDivisionPicker.propTypes = {
  initialBranchId: PropTypes.number,
  initialEnrollmentYearId: PropTypes.number,
  onSelectionChange: PropTypes.func,
  onLoad: PropTypes.func,
  isLoadingLoad: PropTypes.bool,
  autoLoad: PropTypes.bool,
  autoEnrollmentYearId: PropTypes.number,
};

BranchAdministrativeDivisionPicker.defaultProps = {
  initialBranchId: undefined,
  initialEnrollmentYearId: undefined,
  onSelectionChange: undefined,
  onLoad: undefined,
  isLoadingLoad: false,
  autoLoad: false,
  autoEnrollmentYearId: undefined,
};

export default BranchAdministrativeDivisionPicker;
